import React, { useState, useEffect, useRef } from 'react';
import './Search.css';
import { SearchService } from '../../services/searchService';

const Search = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingNext, setIsFetchingNext] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearch = async (query: string, pageNumber: number) => {
    if (!query.trim()) {
      setSearchResults([]);
      setHasMore(false);
      setPage(1);
      return;
    }

    // If first page, show main loader; otherwise show bottom loader
    if (pageNumber === 1) {
      setIsLoading(true);
    } else {
      setIsFetchingNext(true);
    }
    try {
      const result = await SearchService.searchStories({
        searchText: query.trim(),
        pageNumber,
        pageSize: 10,
      });
      const newItems = result?.data?.data || [];
      setHasMore(!!result?.data?.pagination?.hasMore);

      if (pageNumber === 1) {
        setSearchResults(newItems);
      } else {
        setSearchResults((prev) => [...prev, ...newItems]);
      }

      if (pageNumber === 1) {
        setIsLoading(false);
      } else {
        setIsFetchingNext(false);
      }
    } catch (error) {
      console.error('Search error:', error);
      setIsLoading(false);
      setIsFetchingNext(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Reset pagination when query changes
    setPage(1);
    setHasMore(true);
    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      handleSearch(searchQuery, 1);
    }, 500);

    // Cleanup function
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Clear debounce timeout and search immediately
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      setPage(1);
      setHasMore(true);
      handleSearch(searchQuery, 1);
    }
  };

  // Infinite scroll: load next page near bottom
  useEffect(() => {
    const handleScroll = () => {
      if (isLoading || isFetchingNext || !hasMore || !searchQuery.trim()) {
        return;
      }
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const fullHeight = document.documentElement.scrollHeight;

      // Trigger when 200px from bottom
      if (scrollTop + viewportHeight >= fullHeight - 200) {
        const nextPage = page + 1;
        setPage(nextPage);
        handleSearch(searchQuery, nextPage);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [page, hasMore, isLoading, isFetchingNext, searchQuery]);

  return (
    <div className='search-page'>
      <div className='search-container'>
        <div className='search-input-container'>
          <div className='search-input-wrapper'>
            <img
              src='/assets/search.svg'
              alt='Search'
              className='search-input-icon'
            />
            <input
              type='text'
              placeholder='Search for articles...'
              value={searchQuery}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              className='search-input-field'
              autoFocus
            />
            {isLoading && (
              <div className='search-loading'>
                <div className='loading-spinner'></div>
              </div>
            )}
          </div>
        </div>

        {searchQuery && (
          <div className='search-results'>
            <div className='search-results-header'>
              <h2>Search Results</h2>
              <span className='results-count'>
                {isLoading
                  ? 'Searching...'
                  : `${searchResults.length} results found`}
              </span>
            </div>

            {isLoading ? (
              <div className='search-loading-state'>
                <div className='loading-spinner'></div>
                <p>Searching...</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className='search-results-list'>
                {searchResults.map((story: any) => {
                  return (
                    <div className='story-container' key={story.id}>
                      <div
                        className='title'
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <span className='title-heading'>{story.heading}</span>
                      </div>
                      <div
                        className='post-story-description'
                        dangerouslySetInnerHTML={{ __html: story.content }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className='no-results'>
                <img
                  src='/assets/search.svg'
                  alt='No results'
                  className='no-results-icon'
                />
                <h3>No results found</h3>
                <p>Try searching with different keywords</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
